import { usePageTitle } from '@/hooks/usePageTitle';
import PortalNav from '@/components/PortalNav';
import PortalFooter from '@/components/PortalFooter';
import { portalPageStyles } from '@/styles/portalPageStyles';

const sectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  marginBottom: '1rem',
  marginTop: 0,
};

const paragraphStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  marginBottom: '0.75rem',
};

const listStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  paddingLeft: '1.5rem',
  marginBottom: '0.75rem',
};

function TermsOfServicePage() {
  usePageTitle('Terms of Service');

  return (
    <div style={portalPageStyles.pageContainer}>
      <PortalNav />

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2.5rem 2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
          Terms of Service
        </h1>
        <p style={{ ...paragraphStyle, marginBottom: '2rem' }}>
          Last updated: February 12, 2026
        </p>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Service Description</h2>
          <p style={paragraphStyle}>
            Checkademic is a multi-tenant software-as-a-service (SaaS) platform designed for K-12 schools
            to track student behavior and attendance. The Service includes behavior logging, attendance
            tracking, automated parent notifications, reporting tools, and administrative dashboards.
          </p>
          <p style={paragraphStyle}>
            Schools access the Service through dedicated subdomains (e.g., schoolname.checkademic.com)
            with isolated databases for data separation.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Account Registration and Access</h2>
          <p style={paragraphStyle}>
            Access to Checkademic requires an account created by a school administrator or through an
            invitation link. By creating or using an account, you agree to:
          </p>
          <ul style={listStyle}>
            <li>Provide accurate and complete information</li>
            <li>Maintain the security of your login credentials</li>
            <li>Notify your school administrator promptly of any unauthorized use</li>
            <li>Accept responsibility for all activity under your account</li>
          </ul>
          <p style={paragraphStyle}>
            School administrators are responsible for managing user accounts, including creating,
            deactivating, and assigning appropriate roles to their staff and parent users.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Acceptable Use</h2>
          <p style={paragraphStyle}>You agree to use Checkademic only for its intended purpose — tracking and managing student behavior and attendance within a school setting. You agree not to:</p>
          <ul style={listStyle}>
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to other schools' data or accounts</li>
            <li>Upload malicious files, scripts, or content</li>
            <li>Interfere with or disrupt the Service or its infrastructure</li>
            <li>Use automated tools to scrape or extract data from the Service</li>
            <li>Share login credentials with unauthorized individuals</li>
            <li>Use the Service to harass, discriminate, or cause harm to students or staff</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Data Ownership</h2>
          <p style={paragraphStyle}>
            Schools retain full ownership of all data they enter into Checkademic, including student
            records, behavior logs, attendance records, staff information, and configuration settings.
          </p>
          <p style={paragraphStyle}>
            Checkademic does not claim any ownership rights over school data. We process and store your
            data solely to provide and improve the Service as described in our{' '}
            <a href="/privacy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</a>.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Data Protection and Security</h2>
          <p style={paragraphStyle}>
            We implement industry-standard security measures to protect your data, including:
          </p>
          <ul style={listStyle}>
            <li>Database-per-tenant architecture for complete data isolation</li>
            <li>Encryption in transit (TLS/HTTPS) for all communications</li>
            <li>Secure password hashing and token-based authentication</li>
            <li>Role-based access controls</li>
            <li>Regular automated backups</li>
            <li>Comprehensive audit logging</li>
          </ul>
          <p style={paragraphStyle}>
            While we take reasonable precautions to protect your data, no method of electronic
            transmission or storage is 100% secure. Schools are responsible for managing their own
            users' access levels and ensuring appropriate use within their organization.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Service Availability</h2>
          <p style={paragraphStyle}>
            We strive to maintain high availability of the Service but do not guarantee uninterrupted
            access. The Service may be temporarily unavailable due to:
          </p>
          <ul style={listStyle}>
            <li>Scheduled maintenance (we will provide advance notice when possible)</li>
            <li>Unplanned outages or infrastructure issues</li>
            <li>Force majeure events beyond our control</li>
          </ul>
          <p style={paragraphStyle}>
            We will make reasonable efforts to restore service promptly and communicate status updates
            during extended outages.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Communications</h2>
          <p style={paragraphStyle}>
            By using the Service, you acknowledge that Checkademic sends transactional emails
            necessary for service operation (account invitations, password resets, support notifications).
            These communications cannot be opted out of as they are essential to account functionality.
          </p>
          <p style={paragraphStyle}>
            Behavior digest emails sent to parents are opt-in and can be unsubscribed from at any time
            through the notification settings or one-click unsubscribe links included in each email.
          </p>

          <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', marginTop: '1.25rem' }}>
            SMS Text Message Notifications
          </h3>
          <p style={paragraphStyle}>
            By opting in to SMS notifications through the family portal notification settings, you consent
            to receive recurring automated text messages from Checkademic containing behavior and attendance
            digest summaries for your linked students.
          </p>
          <ul style={listStyle}>
            <li><strong>Program:</strong> Checkademic SMS Notifications</li>
            <li><strong>Message frequency varies:</strong> up to 1 message per day (daily digest) or 1 per week (weekly digest)</li>
            <li><strong>Message and data rates may apply</strong></li>
            <li>Reply <strong>STOP</strong> to cancel messages at any time</li>
            <li>Reply <strong>HELP</strong> for assistance</li>
            <li>For support: <a href="mailto:contact@checkademic.com" style={{ color: 'var(--primary-color)' }}>contact@checkademic.com</a></li>
          </ul>
          <p style={paragraphStyle}>
            SMS is not required to use the Service. You may opt out at any time by
            replying <strong>STOP</strong> or by disabling SMS notifications in your notification settings.
            For help, reply <strong>HELP</strong> or contact us
            at <a href="mailto:contact@checkademic.com" style={{ color: 'var(--primary-color)' }}>contact@checkademic.com</a>.
          </p>
          <p style={paragraphStyle}>
            See our <a href="/sms-terms" style={{ color: 'var(--primary-color)' }}>SMS Terms</a> and{' '}
            <a href="/privacy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</a> for more details.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Termination</h2>
          <p style={paragraphStyle}>
            <strong>By the school:</strong> Schools may terminate their use of Checkademic at any time
            by contacting us. Upon termination, we will provide a reasonable period to export data
            before the school's database and all associated data are permanently deleted.
          </p>
          <p style={paragraphStyle}>
            <strong>By Checkademic:</strong> We may suspend or terminate access to the Service if we
            reasonably believe a user or school has violated these Terms, or if required by law. We
            will provide notice before termination except where immediate action is necessary to protect
            the Service or other users.
          </p>
          <p style={paragraphStyle}>
            <strong>Individual users:</strong> School administrators may deactivate individual user
            accounts at any time through the admin dashboard.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Limitation of Liability</h2>
          <p style={paragraphStyle}>
            To the maximum extent permitted by law, Checkademic and its operators shall not be liable
            for any indirect, incidental, special, consequential, or punitive damages arising from or
            related to your use of the Service, including but not limited to loss of data, loss of
            revenue, or interruption of service.
          </p>
          <p style={paragraphStyle}>
            Our total liability for any claims arising from the Service shall not exceed the amount
            paid by your school to Checkademic in the twelve (12) months preceding the claim.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Disclaimer of Warranties</h2>
          <p style={paragraphStyle}>
            The Service is provided "as is" and "as available" without warranties of any kind, either
            express or implied, including but not limited to implied warranties of merchantability,
            fitness for a particular purpose, or non-infringement.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Changes to These Terms</h2>
          <p style={paragraphStyle}>
            We may update these Terms from time to time. We will notify school administrators of
            material changes via email at least 30 days before the changes take effect. Continued use
            of the Service after the effective date constitutes acceptance of the updated Terms.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Governing Law</h2>
          <p style={paragraphStyle}>
            These Terms shall be governed by and construed in accordance with the laws of the United
            States. Any disputes arising from these Terms or the Service shall be resolved through
            good-faith negotiation. If negotiation fails, disputes shall be submitted to binding
            arbitration in accordance with applicable rules.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Contact Us</h2>
          <p style={paragraphStyle}>
            If you have questions about these Terms of Service, please contact us:
          </p>
          <p style={paragraphStyle}>
            Email: <a href="mailto:contact@checkademic.com" style={{ color: 'var(--primary-color)' }}>contact@checkademic.com</a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <PortalFooter currentPage="terms" />
    </div>
  );
}

export default TermsOfServicePage;
